import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type CopyMethod,
  type SessionCopiedEventProps,
  type SessionCopiedFormat,
  type SessionCopySource,
} from '@/models/analytics/features/sessions';
import { logger } from '@/utils/logger/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';

/**
 * Fire `session_copied` for a markdown-native clipboard copy (⌘C / context-menu),
 * the twin of the vMarkdown `ClipboardTextSerializer` firing. The bubble-menu
 * copy goes through the `sessionCopyClicked` Rematch helper; this extension is
 * store-decoupled (Rematch containment ratchet), so it routes through the
 * `track()` facade instead. Same routing as the helper's `session_copied`
 * (`amplitude: false` → Snowplow + Datadog; the Snowplow pipeline forwards to
 * Amplitude) so shortcut copies land in the existing metric, and stamped with
 * `editor_type` for editor segmentation.
 *
 * Never throws — instrumentation must not break a copy.
 */
export const emitSessionCopied = ({
  format,
  copyMethod,
  selectedWordCount,
  totalWordCount,
  copySource = 'editor_clipboard',
}: {
  format: SessionCopiedFormat;
  copyMethod: CopyMethod;
  selectedWordCount: number;
  totalWordCount: number;
  copySource?: SessionCopySource;
}): void => {
  try {
    const percentageCopied =
      totalWordCount > 0
        ? Math.min(100, Math.round((selectedWordCount / totalWordCount) * 100))
        : 0;
    const sessionId = getCurrentSessionId();

    const props: SessionCopiedEventProps = {
      ...(sessionId != null && { session_id: sessionId }),
      format,
      copy_method: copyMethod,
      selected_word_count: selectedWordCount,
      total_word_count: totalWordCount,
      percentage_copied: percentageCopied,
      editor_type: 'markdown_native',
      copy_source: copySource,
      html_flavor_written: true,
    };

    track({
      options: { amplitude: false },
      event_name: TrackingEventName.SessionCopied,
      props,
    });
  } catch (emitError) {
    logger.warn('[markdown-native] copy analytics emit failed', emitError);
  }
};
